import React from 'react';

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  variant?: 'card' | 'inline' | 'centered';
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Shared empty state component used across Categories and Accounts views.
 * Provides consistent messaging when no content is available.
 */
export function EmptyState({ 
  icon, 
  title, 
  description, 
  variant = 'card',
  action 
}: EmptyStateProps) {
  // Variant-specific container styles
  const containerClasses = {
    card: 'rounded-2xl border border-dashed border-border-primary bg-bg-raised px-6 py-12 text-center text-icon-subtle',
    inline: 'px-4 py-6 text-center',
    centered: 'flex items-center justify-center h-full',
  }[variant];

  const content = (
    <div className="text-center">
      {icon && (
        <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-bg-tertiary flex items-center justify-center">
          {icon}
        </div>
      )}
      <p className={variant === 'inline' ? 'text-sm text-icon-subtle' : 'text-base font-medium text-content-primary'}>
        {title}
      </p>
      {description && (
        <p className={variant === 'inline' ? 'text-xs text-icon-subtle mt-0.5' : 'mt-2 text-sm text-icon-subtle'}>
          {description}
        </p>
      )}
      {action && (
        <button
          onClick={action.onClick}
          className="mt-4 px-4 py-2 bg-blueberry-500 text-white rounded-md text-sm font-medium hover:bg-blueberry-600 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );

  if (variant === 'centered') {
    return (
      <div className={containerClasses}>
        {content}
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {content}
    </div>
  );
}
